import {
  doorClient,
  overseaPayClient,
  storeHomeV2Client,
  ImResponse,
  resolve,
  leyanbotClient,
} from '.';

export function packageList() {
  return resolve(doorClient.get<ImResponse<Pay.Package[]>>('/pay/v1/get/package/info'));
}

export interface CalculateLightWeightPriceParams {
  orgId: number;
  orderNum: number;
  newPackageId: Pay.PackageId;
  currency: General.Currency;
}

export interface CalculatePackagePriceParams {
  orgId: number;
  orderType: Pay.OrderType;
  /** -1 表示连续包月（订阅） */
  durationNum: Pay.DurationNum | -1;
  newPackageId: Pay.PackageId;
  currency: General.Currency;
}

export interface CalculateTrafficPackagePriceParams {
  orgId: number;
  newPackageId: Pay.PackageId;
  handleNum: number;
  trafficPackageType: Pay.TrafficPackageType;
  currency: General.Currency;
}

export function calculatePackagePrice(
  params:
    | CalculatePackagePriceParams
    | CalculateTrafficPackagePriceParams
    | CalculateLightWeightPriceParams,
) {
  return resolve(
    overseaPayClient.get<ImResponse<Pay.CalculatePackagePriceRst>>('/calculatePackagePrice', {
      params,
    }),
  );
}

export function packageListV2(orgId: number, orderAction: Pay.OrderType) {
  return resolve(
    overseaPayClient.get<ImResponse<Pay.PackageInfoV2>>('/get_package_info', {
      params: {
        orgId,
        orderAction,
      },
    }),
  );
}

type PackageDurTagText = `promote.package.${number}.tag.duration.${number}.text`;

type PackageTagTextRecord = { [K in PackageDurTagText]: string };

type PromoteJSONData = {
  'promote.title': string;
  'promote.gift': string;
  'promote.ends.in.N.days': string;
  'promote.end.date': string;
  'promote.extra.deal': string;
  'promote.tag.text': string;
  'pormote.banner.text': string;
  'pormote.banner.time.range': string;
} & PackageTagTextRecord;

export interface PromoteConifg {
  /**
   * CHANNEL_ACTIVITY: 渠道活动
   * COMMON_ACTIVITY: 通用活动
   * NONE_ACTIVITY: 没有活动
   */
  activityType: 'CHANNEL_ACTIVITY' | 'COMMON_ACTIVITY' | 'NONE_ACTIVITY' | 'PACKAGE_ACTIVITY';
  startTime: Java.Timestamp; // 活动开始时间
  isNoPopup: boolean; // 是否展示弹窗，1，在倒计时时间内则展示，2，是否在黑名单内，在黑名单内则跳过倒计时弹窗(即为true时跳过，为false不跳过)。跳过即不展示。
  /**
   * @deprecated 揉进isNoPopup由后端处理
   */
  // popupDisplayTime: Java.Timestamp; // 倒计时弹窗 开始展示时间
  endTime: Java.Timestamp; // 活动结束时间
  multiLanguage: {
    language: Record<'cn' | 'en' | 'pt' | 'id' | 'th' | 'vn' | 'tl' | 'es', PromoteJSONData>;
  };
}

export const getPromoteConfig = (orgId: Oversea.Token.Payload['orgId']) => {
  return overseaPayClient.get<ImResponse<PromoteConifg>>('/get_activity_info', {
    params: { orgId },
  });
};

export function orderedPackage(signal?: AbortSignal) {
  return resolve(
    storeHomeV2Client.get<ImResponse<Pay.OrderedPackage[]>>('/admin/combo_info', {
      signal,
    }),
  );
}

export interface OrderAndPayBody {
  idempotent: string;
  accid: string;
  packageType: Pay.PackageType;
  accountNum: number;
  /** 订阅（Stripe 按月自动续费）时传 -1 */
  durationNum: number;
  discountTotalPrice: string;
  orderAction: Pay.OrderType;
  payType: Pay.PayType;
  currency: string;
  packageId: number;
  language: General.SystemLanguage;
  bank?: Pay.PingpongBank;
  /** 是否为订阅（Stripe 按月自动续费） */
  subscription?: boolean;
  /** 订阅取消回调地址 */
  cancelResultUrl?: string;
  /** 支付结果回调地址；订阅时传成功页地址，覆盖默认的 pingpong 回调地址 */
  payResultUrl?: string;
}

export interface OrderAndPayBodyTraffic {
  idempotent: string;
  accid: string;
  discountTotalPrice: string;
  packageType: Pay.PackageType;
  orderAction: Pay.OrderType;
  payType: Pay.PayType;
  currency: string;
  packageId: number;
  handleNum: number;
  trafficPackageType: Pay.TrafficPackageType;
  language: General.SystemLanguage;
  bank?: Pay.PingpongBank;
}

export interface OrderAndPayBodyLightWeight {
  idempotent: string;
  accid: string;
  discountTotalPrice: string;
  packageType: Pay.PackageType;
  orderAction: Pay.OrderType;
  payType: Pay.PayType;
  currency: string;
  packageId: number;
  orderNumber: number;
  language: General.SystemLanguage;
  bank?: Pay.PingpongBank;
}

export type OnOrder = (
  info: OrderAndPayBody | OrderAndPayBodyTraffic | OrderAndPayBodyLightWeight,
) => Promise<any>;

export interface OrderAndPayResponse {
  orderCode: string;
  qrCode: string;
  paymentId: string;
  clientSecret: string;
  paymentUrl: string;
}

export const pingpongBodyTransfer = <T extends { language: General.SystemLanguage }>(body: T) => {
  const {
    location: { origin, pathname },
  } = window;

  return {
    payResultUrl: `${origin}${pathname}#/pingpong`,
    ...body,
    language: {
      'zh-CN': 'cn',
      'en-US': 'en',
      id: 'id',
      th: 'th',
      tl: 'en',
      vi: 'vi',
      es: 'es',
      pt: 'pt',
    }[body.language],
  };
};

const pingpongBankBodyTransfer = <T extends Pick<OrderAndPayBody, 'payType' | 'currency' | 'bank'>>(
  body: T,
) => {
  const { payType, currency, bank } = body;

  return {
    ...body,
    bank: payType === 'pingpong' && currency === 'IDR' ? bank : undefined,
  };
};

export function orderAndPay(
  body: OrderAndPayBody | OrderAndPayBodyTraffic | OrderAndPayBodyLightWeight,
) {
  return resolve(
    doorClient.post<ImResponse<OrderAndPayResponse>>(
      '/pay/v1/create/order/pay',
      pingpongBankBodyTransfer(pingpongBodyTransfer(body)),
    ),
  );
}

export interface PayBody {
  idempotent: string;
  orderCode: string;
  language: General.SystemLanguage;
}

export interface PayResponse {
  qrCode: string;
  paymentId: string;
  clientSecret: string;
  payType: Pay.PayType;
  currency: string;
  paymentUrl: string;
}

export function pay(body: PayBody) {
  return resolve(
    doorClient.post<ImResponse<PayResponse>>('/pay/v1/create/pay', pingpongBodyTransfer(body)),
  );
}

export interface OrderListParams {
  pageNo: number;
  pageSize: number;
}

export interface OrderListResponse {
  pageInfo: {
    pageNo: number;
    pageSize: number;
    total: number;
  };
  orderInfos: Pay.Order[];
}

export function orderList(params: OrderListParams) {
  return resolve(
    doorClient.get<ImResponse<OrderListResponse>>('/pay/v1/query/order/page', { params }),
  );
}

export const getTrafficPkgInfo = (orgId: number) => {
  return resolve(
    overseaPayClient.get<ImResponse<Pay.TrafficPkg>>('/get_traffic_package_info', {
      params: { orgId },
    }),
  );
};

export const getLightWeightPkgInfo = () => {
  return resolve<Pay.LightWeightPkg>(overseaPayClient.get('/get_lightweight_package_info'));
};

export type TrailReportPackType =
  // 试用版
  | 'TRIAL_VERSION'
  // 需要额外付费
  | 'EXTRA_PAYMENT'
  // 不需要额外付费
  | 'NO_EXTRA_PAYMENT';

export interface TrailReportPackInfo {
  dailyOrderCount: number;
  versionId: number;
  version: string;
  expectUsageDay: number;
  currentVersionOrderCount: number;
  currencyAmountList: Array<{ currency: General.Currency; price: number }>;
  type: TrailReportPackType;
}

/**
 * AI回复报告套餐信息
 */
export const getTrailReportPackInfo = () => {
  return resolve(leyanbotClient.get<ImResponse<TrailReportPackInfo>>('/oversea-pay/report_notice'));
};

export interface SubscriptionStatusParams {
  /** 订阅 id；不传时查询当前生效订阅（升级支付验证后轮询用） */
  subscriptionId?: number;
}

/**
 * 查询订阅状态（用于升级订阅支付验证后的结果轮询）
 */
export const subscriptionStatus = (params?: SubscriptionStatusParams) => {
  return resolve(
    leyanbotClient.get<ImResponse<SubscriptionCancelRst>>('/oversea-pay/subscription/status', {
      params,
    }),
  );
};

export interface SubscriptionCancelRst {
  subscriptionId: number;
  paymentChannel: string;
  status: string;
  agreementStatus: string;
  signUrl: string | null;
  packageId: number;
  accountNum: number;
  amountFen: number;
  currency: string;
  cycleMonths: number;
  /** 订购时长；连续包月（订阅）为 -1 */
  durationNum?: number;
  currentPeriodEnd?: string | null;
  nextChargeAt?: string | null;
  graceEndAt?: string | null;
}

/**
 * 取消自动续费（Stripe 期末取消，cancel_at_period_end），当前账期权益仍有效
 */
export const subscriptionCancel = () => {
  return resolve(
    leyanbotClient.post<ImResponse<SubscriptionCancelRst>>('/oversea-pay/subscription/cancel', {}),
  );
};
